import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { simpleGetCallWithErrorResponse, PostCallWithErrorResponse, PutCallWithAuthToken, DeleteCallWithErrorResponseWithTokenQuery } from '../../api/ApiServices';
import Pagination from '../../sharedComponent/Pagination';
import DeleteModal from '../../sharedComponent/DeleteModal';
import { ToastMsg } from '../TostMsg';
import {
  Workflow,
  ShieldCheck,
  TrendingUp,
  Plus,
  Clock,
  CreditCard,
  Users,
  Smartphone,
  MessageSquare,
  Gift,
  CheckCircle2,
  XCircle,
  IndianRupee,
  AlertCircle,
  ShoppingBag,
  Target,
  PieChart,
  Trophy,
  Share2,
  Sparkles,
  ArrowLeft,
  Settings,
  Calendar,
  Star,
  Cake,
  ThumbsUp
} from 'lucide-react';
import '../../assets/css/Wallet.css';


export default function WelletPage() {
  const { sidebar } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'campaigns';

  const setActiveTab = (key) => setSearchParams({ tab: key });

  const renderContent = () => {
    switch (activeTab) {
      case 'campaigns':    return <CampaignSetup />;
      case 'workflows':    return <WorkflowEngine />;
      case 'gamification': return <GamificationEngine />;
      case 'permissions':  return <PermissionsControl />;
      case 'reporting':    return <StrategicReporting />;
      default:             return <CampaignSetup />;
    }
  };

  return (
    <div className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      {/* Page Header */}
      <div className="page-header d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="bg-primary p-2 rounded-3 text-white d-flex align-items-center justify-content-center">
            <CreditCard size={20} />
          </div>
        </div>
        {/* <span className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-secondary)' }}>
          <span className="rounded-circle bg-success d-inline-block" style={{ width: 8, height: 8 }}></span>
          System Operational
        </span> */}
      </div>

      {/* Content */}
      <div className="fade-in">
        {renderContent()}
      </div>
    </div>
  );
}

// ─── Screen 1: Campaigns ──────────────────────────────────────────
function CampaignSetup() {
  const { ApiConfig } = useContext(AppContext);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [viewMode, setViewMode]               = useState('list');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const initialForm = {
    name: '',
    description: '',
    campaign_type: 'first_order',
    credit_value: '',
    value_type: 'flat',
    expiry_value: '',
    expiry_unit: 'Hours',
    global_budget_cap: '',
    wallet_balance_limit: '',
    status: 'active',
    starts_at: '',
    ends_at: '',
  };
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const TYPE_LABELS = {
    first_order: 'First Order',
    'first-order': 'First Order',
    milestone: 'Milestone',
    cross_sell: 'Cross-Sell',
    'cross-sell': 'Cross-Sell',
    streak: 'Streak',
  };

  const formatExpiry = (hours) => {
    const h = Number(hours);
    if (!h && h !== 0) return { value: '', unit: 'Days' };
    if (h % 24 === 0) return { value: h / 24, unit: 'Days' };
    return { value: h, unit: 'Hours' };
  };

  const mapCampaign = (c, idx) => {
    const rawType = (c.campaign_type || '').toString().toLowerCase();
    const valueType = (c.value_type || '').toString().toLowerCase();
    const discountType = valueType === 'percentage' ? 'Percentage Off' : 'Flat Amount';
    const rawStatus = (c.status || '').toString().toLowerCase();
    const exp = formatExpiry(c.expiry_hours);
    return {
      id: c.id ?? idx,
      name: c.name || '-',
      description: c.description || '',
      type: TYPE_LABELS[rawType] || (rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : 'Acquisition'),
      rawType,
      value_type: valueType,
      value: Number(c.credit_value) || 0,
      discountType,
      expValue: exp.value,
      expUnit: exp.unit,
      budget: Number(c.global_budget_cap) || 0,
      maxBalance: Number(c.wallet_balance_limit) || 0,
      status: rawStatus === 'active' ? 'Active' : (c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : 'Inactive'),
      usage: Number(c.budget_used) || 0,
      startsAt: c.starts_at,
      endsAt: c.ends_at,
    };
  };

  const fetchCampaigns = () => {
    const url = ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS;
    if (!url) return;
    setCampaignsLoading(true);
    simpleGetCallWithErrorResponse(`${url}?limit=50&offset=0`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : Array.isArray(json?.data?.campaigns) ? json.data.campaigns : Array.isArray(json) ? json : [];
        setCampaigns(list.map(mapCampaign));
      })
      .catch((err) => console.error('Wallet campaigns fetch error:', err))
      .finally(() => setCampaignsLoading(false));
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS]);

  // ── Pagination state (10 per page, consistent with shared Pagination) ──
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const usersPerPage = 10;

  const filteredCampaigns = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.type || '').toLowerCase().includes(q) ||
      (c.status || '').toLowerCase().includes(q)
    );
  }, [campaigns, searchValue]);

  const startIndex = currentPage * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const pagedCampaigns = filteredCampaigns.slice(startIndex, endIndex);
  const pageChangeHandler = ({ selected }) => setCurrentPage(selected);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    const baseUrl = ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS;
    if (!baseUrl) {
      ToastMsg('error', 'Wallet API endpoint is not configured.');
      return;
    }

    const expiryHours = formData.expiry_unit === 'Days'
      ? Number(formData.expiry_value) * 24
      : Number(formData.expiry_value);

    const toIso = (d) => (d ? new Date(d).toISOString() : null);

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      campaign_type: formData.campaign_type,
      credit_value: Number(formData.credit_value),
      value_type: formData.value_type,
      expiry_hours: expiryHours,
      global_budget_cap: Number(formData.global_budget_cap),
      wallet_balance_limit: Number(formData.wallet_balance_limit),
      status: formData.status,
      starts_at: toIso(formData.starts_at),
      ends_at: toIso(formData.ends_at),
    };

    const isEdit = editingId != null;
    const request = isEdit
      ? PutCallWithAuthToken(`${baseUrl}/${editingId}`, JSON.stringify(payload))
      : PostCallWithErrorResponse(baseUrl, JSON.stringify(payload));

    setSubmitting(true);
    request
      .then((res) => {
        if (!res?.error && (res?.json?.success || res?.json?.data)) {
          ToastMsg('success', res?.json?.message || (isEdit ? 'Campaign updated successfully' : 'Campaign created successfully'));
          setFormData(initialForm);
          setEditingId(null);
          setViewMode('list');
          setSelectedDetail(null);
          fetchCampaigns();
        } else {
          const errMsg = res?.json?.message
            || (Array.isArray(res?.json?.errors) ? res.json.errors.map((e) => e.msg).join(', ') : null)
            || (isEdit ? 'Failed to update campaign' : 'Failed to create campaign');
          ToastMsg('error', errMsg);
        }
      })
      .catch((err) => {
        console.error(isEdit ? 'Update campaign error:' : 'Create campaign error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  // Open the form pre-filled from a fetched detail object.
  // Accepts the raw API shape (snake_case) so we can call it directly with `selectedDetail`.
  const openEditCampaign = (raw) => {
    if (!raw) return;
    // datetime-local needs "YYYY-MM-DDTHH:mm" — strip seconds/Z from ISO.
    const toLocalDt = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const exp = formatExpiry(raw.expiry_hours);
    setFormData({
      name: raw.name || '',
      description: raw.description || '',
      campaign_type: (raw.campaign_type || 'acquisition').toLowerCase(),
      credit_value: raw.credit_value != null ? String(Number(raw.credit_value)) : '',
      value_type: (raw.value_type || 'flat').toLowerCase(),
      expiry_value: exp.value === '' ? '' : String(exp.value),
      expiry_unit: exp.unit || 'Hours',
      global_budget_cap: raw.global_budget_cap != null ? String(Number(raw.global_budget_cap)) : '',
      wallet_balance_limit: raw.wallet_balance_limit != null ? String(Number(raw.wallet_balance_limit)) : '',
      status: (raw.status || 'active').toLowerCase(),
      starts_at: toLocalDt(raw.starts_at),
      ends_at: toLocalDt(raw.ends_at),
    });
    setEditingId(raw.id);
    setViewMode('create');
  };

  const fetchCampaignDetail = (id) => {
    const baseUrl = ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS;
    if (!baseUrl || id == null) return;
    setDetailLoading(true);
    setSelectedDetail(null);
    simpleGetCallWithErrorResponse(`${baseUrl}/${id}`)
      .then((res) => {
        console.log('Campaign detail response:', res?.json);
        const json = res?.json;
        const data = json?.data ?? json;
        setSelectedDetail(data);
      })
      .catch((err) => {
        console.error('Campaign detail fetch error:', err);
        ToastMsg('error', 'Failed to load campaign details.');
      })
      .finally(() => setDetailLoading(false));
  };

  const viewCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setViewMode('view');
    fetchCampaignDetail(campaign?.id);
  };

  // Shared style helpers — declared above conditional returns so the create/view/list
  // blocks all have access (avoids TDZ when an early return runs before the list view).
  // NOTE: bundle.css ships a global `.badge { position:absolute; background:red; border-radius:50% }`
  // (used for notification dots) that clobbers Bootstrap's pill badges. So we render our own
  // inline-styled "pills" here instead of using the `badge` class.
  const typeStyles = {
    'First Order': { bg: '#e0f2fe', color: '#075985' },
    'Milestone':   { bg: '#dbeafe', color: '#1e40af' },
    'Cross-Sell':  { bg: '#fef3c7', color: '#92400e' },
    'Streak':      { bg: '#ede9fe', color: '#6d28d9' },
  };
  const defaultTypeStyle = { bg: '#f1f5f9', color: '#475569' };
  const pillStyle = (extra = {}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.72rem',
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    ...extra,
  });

  // ── Create Form ──
  if (viewMode === 'create') {
    // Custom styles — bypass broken Bootstrap input-group from global CSS
    const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: 6, display: 'block', letterSpacing: '0.01em' };
    const fieldHelp = { fontSize: '0.72rem', color: '#94a3b8', marginTop: 4, display: 'block' };
    const inputBase = {
      width: '100%',
      padding: '9px 12px',
      borderRadius: 8,
      border: '1px solid #e2e8f0',
      background: '#fff',
      fontSize: '0.875rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxSizing: 'border-box',
    };
    const prefixWrap = {
      display: 'flex',
      alignItems: 'stretch',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#fff',
    };
    const prefixBox = {
      padding: '9px 12px',
      background: '#f8fafc',
      borderRight: '1px solid #e2e8f0',
      color: '#475569',
      fontWeight: 600,
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
    };
    const prefixInput = {
      flex: 1,
      border: 'none',
      padding: '9px 12px',
      fontSize: '0.875rem',
      color: '#1e293b',
      outline: 'none',
      background: 'transparent',
    };
    const sectionTitle = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 };

    const isEdit = editingId != null;
    const exitForm = () => {
      setViewMode(isEdit ? 'view' : 'list');
      setEditingId(null);
      setFormData(initialForm);
    };

    return (
      <div className="d-flex flex-column gap-4">
        <div className="d-flex align-items-center gap-3 mb-2">
          <button type="button" onClick={exitForm} className="btn btn-light text-secondary">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{isEdit ? 'Edit Campaign' : 'Create New Campaign'}</h5>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>
              {isEdit ? 'Update rules and financial exposure for this promotion.' : 'Configure rules and financial exposure for your new promotion.'}
            </p>
          </div>
          {isEdit && (
            <span className="ms-2" style={pillStyle({ background: '#f1f5f9', color: '#64748b', fontSize: '0.7rem' })}>#{editingId}</span>
          )}
        </div>

        <form onSubmit={handleCreateSubmit} className="shadow-sm border bg-white rounded-4" style={{ overflow: 'hidden' }}>
          {/* Section: Basics */}
          <div className="p-4 p-md-4 border-bottom">
            <div style={sectionTitle}>Basics</div>
            <div className="row g-3">
              <div className="col-md-8">
                <label style={fieldLabel}>Campaign Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input required type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., Milestone: 5 orders" style={inputBase} />
              </div>
              <div className="col-md-4">
                <label style={fieldLabel}>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange} style={inputBase}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
              <div className="col-12">
                <label style={fieldLabel}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Reward loyal customers who hit 5 completed orders"
                  style={{ ...inputBase, resize: 'vertical', minHeight: 70 }}
                />
              </div>
              <div className="col-md-6">
                <label style={fieldLabel}>Campaign Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select required name="campaign_type" value={formData.campaign_type} onChange={handleInputChange} style={inputBase}>
                  <option value="first_order">First Order</option>
                  <option value="milestone">Milestone</option>
                  <option value="cross_sell">Cross-Sell</option>
                  <option value="streak">Streak</option>
                </select>
              </div>
              <div className="col-md-6">
                <label style={fieldLabel}>Value Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select required name="value_type" value={formData.value_type} onChange={handleInputChange} style={inputBase}>
                  <option value="flat">Flat Amount</option>
                  <option value="percentage">Percentage Off</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section: Reward & Limits */}
          <div className="p-4 border-bottom">
            <div style={sectionTitle}>Reward & Limits</div>
            <div className="row g-3">
              <div className="col-md-4">
                <label style={fieldLabel}>Credit Value <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={prefixWrap}>
                  <div style={prefixBox}>{formData.value_type === 'percentage' ? '%' : '₹'}</div>
                  <input required type="number" min="0" step="0.01" name="credit_value" value={formData.credit_value} onChange={handleInputChange} placeholder="Enter credit value" style={prefixInput} />
                </div>
              </div>
              <div className="col-md-4">
                <label style={fieldLabel}>Global Budget Cap <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={prefixWrap}>
                  <div style={prefixBox}>₹</div>
                  <input required type="number" min="0" step="0.01" name="global_budget_cap" value={formData.global_budget_cap} onChange={handleInputChange} placeholder="Enter total budget cap" style={prefixInput} />
                </div>
                <span style={fieldHelp}>Pauses automatically when total issued hits this limit.</span>
              </div>
              <div className="col-md-4">
                <label style={fieldLabel}>Wallet Balance Limit (Max) <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={prefixWrap}>
                  <div style={prefixBox}>₹</div>
                  <input required type="number" min="0" step="0.01" name="wallet_balance_limit" value={formData.wallet_balance_limit} onChange={handleInputChange} placeholder="Enter max wallet balance" style={prefixInput} />
                </div>
                <span style={fieldHelp}>Prevents promo-stacking on a single wallet.</span>
              </div>
            </div>
          </div>

          {/* Section: Schedule */}
          <div className="p-4 border-bottom">
            <div style={sectionTitle}>Schedule</div>
            <div className="row g-3">
              <div className="col-md-4">
                <label style={fieldLabel}>Expiration <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input required type="number" min="1" name="expiry_value" value={formData.expiry_value} onChange={handleInputChange} placeholder="Enter duration" style={{ ...inputBase, flex: 1 }} />
                  <select name="expiry_unit" value={formData.expiry_unit} onChange={handleInputChange} style={{ ...inputBase, width: 100 }}>
                    <option value="Hours">Hours</option>
                    <option value="Days">Days</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <label style={fieldLabel}>Starts At</label>
                <input type="datetime-local" name="starts_at" value={formData.starts_at} onChange={handleInputChange} style={inputBase} />
              </div>
              <div className="col-md-4">
                <label style={fieldLabel}>Ends At</label>
                <input type="datetime-local" name="ends_at" value={formData.ends_at} onChange={handleInputChange} style={inputBase} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 px-4 d-flex justify-content-end gap-2" style={{ background: '#f8fafc' }}>
            <button type="button" onClick={exitForm} disabled={submitting} className="btn btn-light fw-medium">Cancel</button>
            <button type="submit" disabled={submitting} className="btn btn-primary fw-medium px-4 d-flex align-items-center gap-2">
              {submitting && (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14 }}></span>
              )}
              {submitting ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Campaign' : 'Save Campaign')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Detail View ──
  if (viewMode === 'view' && selectedCampaign) {
    // Prefer the freshly-fetched detail; fall back to row data while loading.
    const detail = selectedDetail ? mapCampaign(selectedDetail, 0) : selectedCampaign;
    const ratio = detail.budget ? (detail.usage / detail.budget) * 100 : 0;
    const usagePercent = Math.min(100, Math.round(ratio));
    const displayPct = ratio > 0 && usagePercent === 0 ? 2 : usagePercent;
    const remaining = Math.max(0, (Number(detail.budget) || 0) - (Number(detail.usage) || 0));
    const isActive = detail.status === 'Active';
    const valueLabel = detail.discountType === 'Percentage Off'
      ? `${detail.value}%`
      : `₹${Number(detail.value).toLocaleString()}`;

    const fmtDate = (iso) => {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    };

    const statPillStyle = pillStyle({
      background: isActive ? '#dcfce7' : '#f1f5f9',
      color: isActive ? '#15803d' : '#475569',
      padding: '5px 12px',
      fontSize: '0.75rem',
    });

    const fieldLabel = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
    const fieldValue = { fontSize: '0.9rem', fontWeight: 500, color: '#1e293b', margin: 0 };

    return (
      <div className="d-flex flex-column gap-4">
        {/* Header */}
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <button type="button" onClick={() => { setViewMode('list'); setSelectedDetail(null); }} className="btn btn-light text-secondary">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{detail.name}</h5>
              {detail.id != null && (
                <span style={pillStyle({ background: '#f1f5f9', color: '#64748b', fontSize: '0.7rem' })}>#{detail.id}</span>
              )}
              {detailLoading && (
                <span className="spinner-border spinner-border-sm text-secondary" role="status" style={{ width: 14, height: 14 }}></span>
              )}
            </div>
            {detail.description && (
              <p className="small mb-0 mt-1" style={{ color: 'var(--text-secondary)' }}>{detail.description}</p>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={statPillStyle}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: isActive ? '#16a34a' : '#94a3b8' }}></span>
              {detail.status}
            </span>
            {/* <button className="btn btn-sm btn-outline-danger fw-medium px-3">Pause</button> */}
            <button
              type="button"
              onClick={() => openEditCampaign(selectedDetail || { id: detail.id, name: detail.name })}
              disabled={detailLoading || !selectedDetail}
              className="btn btn-sm btn-primary fw-medium px-3"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Top stats strip */}
        <div className="row g-3">
          {[
            { label: 'Credit Value', value: valueLabel, sub: detail.discountType },
            { label: 'Budget Cap', value: `₹${Number(detail.budget).toLocaleString()}`, sub: 'Global' },
            { label: 'Used', value: `₹${Number(detail.usage).toLocaleString()}`, sub: `${usagePercent}% of cap` },
            { label: 'Wallet Balance Limit', value: `₹${Number(detail.maxBalance).toLocaleString()}`, sub: 'Per wallet' },
          ].map((s) => (
            <div className="col-6 col-md-3" key={s.label}>
              <div className="border bg-white rounded-3 p-3 h-100 shadow-sm">
                <div style={fieldLabel}>{s.label}</div>
                <div className="fs-5 fw-bold mt-1" style={{ color: '#0f172a' }}>{s.value}</div>
                <div className="small mt-1" style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4">
          {/* Configuration card */}
          <div className="col-lg-8">
            <div className="border bg-white rounded-4 shadow-sm h-100">
              <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2">
                <Settings size={16} className="text-primary" />
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Configuration</h6>
              </div>
              <div className="p-4">
                <div className="row g-4">
                  {[
                    { label: 'Campaign Type', value: detail.type },
                    { label: 'Value Type', value: detail.discountType },
                    { label: 'Credit Value', value: valueLabel },
                    { label: 'Expiration', value: detail.expValue !== '' ? `${detail.expValue} ${detail.expUnit}` : '—' },
                    { label: 'Global Budget Cap', value: `₹${Number(detail.budget).toLocaleString()}` },
                    { label: 'Wallet Balance Limit', value: `₹${Number(detail.maxBalance).toLocaleString()}` },
                    { label: 'Starts At', value: fmtDate(detail.startsAt) },
                    { label: 'Ends At', value: fmtDate(detail.endsAt) },
                  ].map(({ label, value }) => (
                    <div className="col-sm-6" key={label}>
                      <p style={fieldLabel}>{label}</p>
                      <p style={fieldValue}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Audit row */}
                {(selectedDetail?.created_at || selectedDetail?.updated_at || selectedDetail?.created_by) && (
                  <div className="mt-4 pt-3 border-top d-flex flex-wrap gap-4">
                    {selectedDetail?.created_at && (
                      <div>
                        <p style={fieldLabel}>Created</p>
                        <p style={{ ...fieldValue, fontSize: '0.8rem' }}>{fmtDate(selectedDetail.created_at)}</p>
                      </div>
                    )}
                    {selectedDetail?.updated_at && (
                      <div>
                        <p style={fieldLabel}>Updated</p>
                        <p style={{ ...fieldValue, fontSize: '0.8rem' }}>{fmtDate(selectedDetail.updated_at)}</p>
                      </div>
                    )}
                    {selectedDetail?.created_by != null && (
                      <div>
                        <p style={fieldLabel}>Created By</p>
                        <p style={{ ...fieldValue, fontSize: '0.8rem' }}>User #{selectedDetail.created_by}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Budget Utilization card */}
          <div className="col-lg-4">
            <div className="border bg-white rounded-4 shadow-sm h-100 d-flex flex-column">
              <div className="px-4 py-3 border-bottom">
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Budget Utilization</h6>
              </div>
              <div className="p-4 flex-grow-1 d-flex flex-column justify-content-center">
                <div className="d-flex justify-content-between align-items-end mb-2">
                  <span className="fs-3 fw-bold" style={{ color: '#0f172a' }}>₹{Number(detail.usage).toLocaleString()}</span>
                  <span className="small" style={{ color: '#94a3b8' }}>/ ₹{Number(detail.budget).toLocaleString()}</span>
                </div>
                <div className="progress mb-2" style={{ height: '0.5rem', backgroundColor: '#e2e8f0' }}>
                  <div className={`progress-bar ${usagePercent > 90 ? 'bg-danger' : usagePercent > 75 ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${displayPct}%` }}></div>
                </div>
                <div className="d-flex justify-content-between small" style={{ color: '#64748b' }}>
                  <span>{usagePercent}% used</span>
                  <span>₹{remaining.toLocaleString()} remaining</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List View ── (typeStyles / pillStyle are declared above so all branches share them)
  return (
    <div className="d-flex flex-column gap-4 position-relative pb-5">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Wallet Campaigns</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>
            Manage promotional rules and financial exposure.
            {!campaignsLoading && (
              <span className="ms-2" style={pillStyle({ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' })}>
                {filteredCampaigns.length} total
              </span>
            )}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
      
          <button onClick={() => setViewMode('create')} className="btn btn-primary d-flex align-items-center gap-2 fw-medium">
            <Plus size={16} /> Create Campaign
          </button>
        </div>
      </div>
      <div className="data-table-wrapper shadow-sm rounded-4 border bg-white overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr style={{ fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <th className="ps-4 py-3" style={{ width: 60 }}>#</th>
                <th className="py-3">Campaign</th>
                <th className="py-3">Type</th>
                <th className="py-3">Value</th>
                <th className="py-3">Expiry</th>
                <th className="py-3" style={{ minWidth: 180 }}>Budget Usage</th>
                <th className="py-3">Status</th>
                <th className="py-3 pe-4 text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedCampaigns.map((camp, i) => {
                const ratio = camp.budget ? (camp.usage / camp.budget) * 100 : 0;
                const pct = Math.min(100, Math.round(ratio));
                // For non-zero but sub-1% usage, show a thin visible sliver so the color is depicted.
                const displayPct = ratio > 0 && pct === 0 ? 2 : pct;
                const barClass = pct > 90 ? 'bg-danger' : pct > 75 ? 'bg-warning' : pct > 0 ? 'bg-primary' : 'bg-light';
                return (
                  <tr key={camp.id}>
                    <td className="ps-4 small" style={{ color: 'var(--text-secondary)' }}>{startIndex + i + 1}</td>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="fw-semibold" style={{ color: 'var(--text-primary)' }}>{camp.name}</span>
                        {camp.description && (
                          <span className="small text-truncate" style={{ color: 'var(--text-secondary)', maxWidth: 280, fontSize: '0.75rem' }}>{camp.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const ts = typeStyles[camp.value_type] || defaultTypeStyle;
                        return (
                          <span style={pillStyle({ background: ts.bg, color: ts.color })}>
                            {camp.value_type || '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="fw-medium" style={{ color: 'var(--text-primary)' }}>
                      {camp.discountType === 'Percentage Off' ? `${camp.value}% Off` : `₹${Number(camp.value).toLocaleString()} Flat`}
                    </td>
                    <td className="small" style={{ color: 'var(--text-secondary)' }}>
                      {camp.expValue !== '' ? `${camp.expValue} ${camp.expUnit}` : '—'}
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1" style={{ width: 200 }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                          ₹{Number(camp.usage).toLocaleString()} / ₹{Number(camp.budget).toLocaleString()}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress flex-grow-1" style={{ height: '0.35rem', backgroundColor: '#e2e8f0' }}>
                            <div className={`progress-bar ${barClass}`} style={{ width: `${displayPct}%` }}></div>
                          </div>
                          <span className="fw-semibold" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', minWidth: 32, textAlign: 'right' }}>{pct}%</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const isActive = camp.status === 'Active';
                        return (
                          <span style={pillStyle({
                            background: isActive ? '#dcfce7' : '#f1f5f9',
                            color: isActive ? '#15803d' : '#475569',
                          })}>
                            <span style={{
                              display: 'inline-block',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: isActive ? '#16a34a' : '#94a3b8',
                            }}></span>
                            {camp.status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="text-end pe-4">
                      <button onClick={() => viewCampaign(camp)} className="btn btn-sm btn-outline-primary fw-medium" style={{ fontSize: '0.75rem' }}>
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {campaignsLoading && (
                <tr><td colSpan="8" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>Loading campaigns...</td></tr>
              )}
              {!campaignsLoading && filteredCampaigns.length === 0 && (
                <tr><td colSpan="8" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>
                  {searchValue ? 'No campaigns match your search.' : 'No campaigns found. Create one to get started.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {filteredCampaigns.length > usersPerPage && (
        <Pagination
          data={campaigns}
          pageChangeHandler={pageChangeHandler}
          usersPerPage={usersPerPage}
          currentPage={currentPage}
          searchValue={searchValue}
          searchedDataPageCounts={filteredCampaigns}
        />
      )}
    </div>
  );
}

// ─── Screen 2: Workflows ──────────────────────────────────────────
function WorkflowEngine() {
  const [selectedWorkflow, setSelectedWorkflow] = useState('Phone to App');

  const workflowOptions = [
    { value: 'Phone to App', label: 'Phone to App Conversion' },
    { value: 'Cart Abandon',  label: 'Cart Abandonment' },
    { value: 'Win-Back',      label: '30-Day Win-Back' },
    { value: 'Second Visit',  label: 'Second Visit Guarantee' },
    { value: 'Feedback',      label: 'Post-Order Feedback' },
    { value: 'Birthday',      label: 'Birthday Reward' },
  ];

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Automated "Nudge" Workflows</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Design behavior-driven triggers and automated actions.</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex gap-2">
            <select value={selectedWorkflow} onChange={(e) => setSelectedWorkflow(e.target.value)} className="form-select bg-white" style={{ minWidth: 260 }}>
              {workflowOptions.map(o => <option key={o.value} value={o.value}>Workflow: {o.label}</option>)}
            </select>
            <button className="btn btn-outline-secondary fw-medium bg-white">Edit</button>
          </div>
          <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
            <input className="form-check-input mt-0" type="checkbox" role="switch" id="workflowToggle" defaultChecked style={{ width: '2.5rem', height: '1.25rem' }} />
            <label className="form-check-label small fw-bold text-success mb-0" htmlFor="workflowToggle">ACTIVE</label>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-4 p-4 p-md-5 d-flex flex-column align-items-center" style={{ minHeight: 600 }}>
        <WorkflowDiagram type={selectedWorkflow} />
      </div>
    </div>
  );
}

function WorkflowDiagram({ type }) {
  const Connector = () => (
    <div className="border-start border-secondary" style={{ height: '2rem', width: 1 }}></div>
  );
  const TimerBadge = ({ label }) => (
    <div className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill px-3 py-2 d-flex align-items-center gap-1">
      <Clock size={14} /> {label}
    </div>
  );
  const ConditionCard = ({ label }) => (
    <div className="card bg-dark text-white rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <Workflow size={20} className="text-secondary" />
          <span className="fw-medium">Condition Check</span>
        </div>
        <span className="badge bg-secondary rounded-pill fw-normal px-2 py-1">{label}</span>
      </div>
    </div>
  );
  const YesNoSplit = () => (
    <div className="d-flex w-100 justify-content-center position-relative" style={{ maxWidth: '28rem' }}>
      <div className="w-50 border-top border-start border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopLeftRadius: '0.5rem' }}>
        <div className="position-absolute top-0 start-0 translate-middle bg-white px-2 small fw-bold text-success d-flex align-items-center gap-1"><CheckCircle2 size={14}/> YES</div>
      </div>
      <div className="w-50 border-top border-end border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopRightRadius: '0.5rem' }}>
        <div className="position-absolute top-0 end-0 translate-middle bg-white px-2 small fw-bold text-danger d-flex align-items-center gap-1"><XCircle size={14}/> NO</div>
      </div>
    </div>
  );
  const EndCard = ({ label }) => (
    <div className="flex-grow-1 card border rounded-4 p-3 shadow-sm text-center d-flex flex-column align-items-center justify-content-center" style={{ maxWidth: 280, minHeight: 120, opacity: 0.7 }}>
      <CheckCircle2 size={24} className="text-secondary mb-2" />
      <h6 className="fw-medium mb-1" style={{ color: 'var(--text-primary)' }}>End Workflow</h6>
      <p className="small mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{label}</p>
    </div>
  );
  const NudgeCard = ({ credit, expires, sms }) => (
    <div className="flex-grow-1 card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative" style={{ maxWidth: 320 }}>
      <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
        The Nudge
      </div>
      <div className="d-flex flex-column gap-3 mt-3">
        <div className="d-flex align-items-start gap-2">
          <Gift size={18} className="text-success flex-shrink-0 mt-1" />
          <div>
            <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{credit}</h6>
            <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{expires}</p>
          </div>
        </div>
        <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
          <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
          <div>
            <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Automated SMS</h6>
            <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{sms}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (type === 'Phone to App') return (
    <>
      <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-primary p-2 rounded-circle text-white mt-1"><Smartphone size={20} /></div>
          <div>
            <span className="small fw-bold text-primary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Phone order placed via Agent App</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer calls in and agent places order under their phone number.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-light p-2 rounded-circle text-secondary mt-1"><MessageSquare size={20} /></div>
          <div>
            <span className="small fw-bold text-secondary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Immediate Action</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Send standard SMS link</h6>
            <div className="bg-light border p-2 rounded-2 mt-2 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              "Thanks for ordering! Track your order and reorder easily by downloading our app: [Link]"
            </div>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 3 Days" /><Connector />
      <ConditionCard label="Has user logged into App?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="Customer successfully converted." />
        <NudgeCard credit="Deposit 10% Discount Credit" expires="Applies to phone-linked wallet profile. Expires in 48h." sms='"Get 10% off your next meal if you order via the app today! Click here: [Link]"' />
      </div>
    </>
  );

  if (type === 'Cart Abandon') return (
    <>
      <div className="card border-info-subtle bg-info-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-info text-white p-2 rounded-circle mt-1"><ShoppingBag size={20} /></div>
          <div>
            <span className="small fw-bold text-info-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Items left in Cart</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>User added items to their basket but exited the app without checking out.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-light p-2 rounded-circle text-secondary mt-1"><MessageSquare size={20} /></div>
          <div>
            <span className="small fw-bold text-secondary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Immediate Action</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Send standard Push Notification</h6>
            <div className="bg-light border p-2 rounded-2 mt-2 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              "You left something delicious in your cart! Tap to complete your order before it gets cold."
            </div>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 3 Days" /><Connector />
      <ConditionCard label="Has user completed a purchase?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="Customer completed checkout organically." />
        <NudgeCard credit="Deposit 5% Discount Credit" expires="Applies to their wallet profile. Expires in 48h." sms='"Still hungry? We added a 5% off credit to your wallet to help you finish your order today! Tap here to checkout."' />
      </div>
    </>
  );

  if (type === 'Win-Back') return (
    <>
      <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-primary p-2 rounded-circle text-white mt-1"><Calendar size={20} /></div>
          <div>
            <span className="small fw-bold text-primary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>30 Days Inactive</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer has not placed an order or engaged with the app in 30 days.</p>
          </div>
        </div>
      </div>
      <Connector />
      <ConditionCard label="Has user opened app recently?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="User is still browsing, hold promotion." />
        <NudgeCard credit="Deposit 20% Win-Back Credit" expires="High-value incentive. Expires in 7 Days." sms={"\"It's been a while! We've added a special 20% discount to your wallet. Come back and treat yourself this week!\""} />
      </div>
    </>
  );

  if (type === 'Second Visit') return (
    <>
      <div className="card border-warning-subtle bg-warning-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-warning text-dark p-2 rounded-circle mt-1"><Star size={20} /></div>
          <div>
            <span className="small fw-bold text-warning-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>First App Order Completed</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer has successfully received their very first order via the App.</p>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 24 Hours" /><Connector />
      <div className="card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative w-100" style={{ maxWidth: '28rem' }}>
        <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>Habit Builder</div>
        <div className="d-flex flex-column gap-3 mt-3">
          <div className="d-flex align-items-start gap-2">
            <Gift size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit ₹50 Flat Credit</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Guarantees a 2nd visit. Expires in 7 Days.</p>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
            <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Thank You SMS/Push</h6>
              <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "Thanks for trying our app! Here is ₹50 on us for your next meal. Valid for the next 7 days."
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (type === 'Feedback') return (
    <>
      <div className="card border-info-subtle bg-info-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-info text-white p-2 rounded-circle mt-1"><ShoppingBag size={20} /></div>
          <div>
            <span className="small fw-bold text-info-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Order Marked "Delivered"</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Delivery partner confirms drop-off.</p>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 2 Hours" /><Connector />
      <div className="card bg-dark text-white rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2"><Workflow size={20} className="text-secondary" /><span className="fw-medium">Condition Check</span></div>
          <span className="badge bg-secondary rounded-pill fw-normal px-2 py-1">Did customer rate the order?</span>
        </div>
      </div>
      <div className="d-flex w-100 justify-content-center position-relative" style={{ maxWidth: '28rem' }}>
        <div className="w-50 border-top border-start border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopLeftRadius: '0.5rem' }}>
          <div className="position-absolute top-0 start-0 translate-middle bg-white px-2 small fw-bold text-success d-flex align-items-center gap-1"><CheckCircle2 size={14}/> YES</div>
        </div>
        <div className="w-50 border-top border-end border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopRightRadius: '0.5rem' }}>
          <div className="position-absolute top-0 end-0 translate-middle bg-white px-2 small fw-bold text-danger d-flex align-items-center gap-1"><XCircle size={14}/> NO</div>
        </div>
      </div>
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <div className="flex-grow-1 card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative" style={{ maxWidth: 280 }}>
          <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>Surprise Reward</div>
          <div className="d-flex align-items-start gap-2 mt-3">
            <ThumbsUp size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit 5% Cashback</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Reward positive behavior instantly.</p>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 card border rounded-4 p-3 shadow-sm" style={{ maxWidth: 320 }}>
          <div className="d-flex align-items-start gap-2">
            <MessageSquare size={18} className="text-secondary flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Review Request SMS</h6>
              <div className="bg-light border p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "How was your food? Rate your order to unlock a surprise wallet reward! [Link]"
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (type === 'Birthday') return (
    <>
      <div className="card rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem', background: 'linear-gradient(135deg, #ec4899, #a855f7)', color: 'white' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-white text-dark p-2 rounded-circle mt-1"><Cake size={20} /></div>
          <div>
            <span className="small fw-bold text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1 text-white">Date Matches Birthday</h6>
            <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.7)' }}>Today matches the birth date on the customer's profile.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative w-100" style={{ maxWidth: '28rem' }}>
        <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>VIP Treatment</div>
        <div className="d-flex flex-column gap-3 mt-3">
          <div className="d-flex align-items-start gap-2">
            <Gift size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit ₹200 Birthday Credit</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>High perceived value. Valid for Birthday Week.</p>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
            <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Celebration SMS/Push</h6>
              <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "Happy Birthday from ZamZam! Enjoy ₹200 off your celebration meal. Valid all week long!"
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return null;
}

// ─── Screen 3: Gamification ───────────────────────────────────────
const RULE_TYPES = [
  { value: 'milestone',   label: 'Milestone',         icon: Trophy,      tint: { bg: '#fef3c7', color: '#92400e' }, description: 'Reward customers after N completed orders.' },
  { value: 'first_order', label: 'First Order',       icon: Star,        tint: { bg: '#e0f2fe', color: '#075985' }, description: 'One-time welcome credit on first successful order.' },
  { value: 'cross_sell',  label: 'Cross-Sell',        icon: Target,      tint: { bg: '#dbeafe', color: '#1e40af' }, description: 'Push a specific menu category alongside the main meal.' },
  { value: 'referral',    label: 'Refer & Earn',      icon: Share2,      tint: { bg: '#ede9fe', color: '#6d28d9' }, description: 'Reward users on successful referral conversion.' },
];

function GamificationEngine() {
  const { ApiConfig } = useContext(AppContext);
  const baseUrl = ApiConfig?.GET_WALLET_ADMIN_GAMIFICATION_RULES;
  const campaignsUrl = ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS;

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [campaignOptions, setCampaignOptions] = useState([]);

  const initialForm = {
    name: '',
    rule_type: 'milestone',
    status: 'active',
    reward_campaign_id: '',
    // milestone
    orders_required: '',
    // shared reward
    reward_amount: '',
    // cross_sell
    trigger_category_id: '',
    trigger_category: '',
  };
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busyRuleId, setBusyRuleId] = useState(null); // for inline pause/delete spinners
  const [ruleDetailLoading, setRuleDetailLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // rule pending delete-confirmation

  // Shared style helpers (mirrors CampaignSetup; bundle.css's global .badge rule blocks Bootstrap badges)
  const pillStyle = (extra = {}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.72rem',
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    ...extra,
  });
  const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: 6, display: 'block', letterSpacing: '0.01em' };
  const inputBase = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#fff',
    fontSize: '0.875rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const sectionTitle = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 };

  // ── API ──────────────────────────────────────────────────────────
  const fetchRules = () => {
    if (!baseUrl) return;
    setRulesLoading(true);
    const url = activeOnly ? `${baseUrl}?active_only=true` : baseUrl;
    simpleGetCallWithErrorResponse(url)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        setRules(list);
      })
      .catch((err) => console.error('Gamification rules fetch error:', err))
      .finally(() => setRulesLoading(false));
  };

  const fetchCampaignOptions = () => {
    if (!campaignsUrl) return;
    simpleGetCallWithErrorResponse(`${campaignsUrl}?limit=100&offset=0`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : Array.isArray(json?.data?.campaigns) ? json.data.campaigns : Array.isArray(json) ? json : [];
        setCampaignOptions(list.map((c) => ({ id: c.id, name: c.name })));
      })
      .catch((err) => console.error('Campaign options fetch error:', err));
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, activeOnly]);

  useEffect(() => {
    fetchCampaignOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsUrl]);

  // ── Helpers ──────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildConfig = () => {
    switch (formData.rule_type) {
      case 'milestone':
        return {
          orders_required: Number(formData.orders_required),
          reward_amount: Number(formData.reward_amount),
        };
      case 'first_order':
      case 'referral':
        return { reward_amount: Number(formData.reward_amount) };
      case 'cross_sell':
        return {
          trigger_category_id: Number(formData.trigger_category_id),
          trigger_category: formData.trigger_category.trim(),
          reward_amount: Number(formData.reward_amount),
        };
      default:
        return {};
    }
  };

  const openCreate = () => {
    setFormData(initialForm);
    setEditingId(null);
    setShowForm(true);
  };

  const fillFormFromRule = (rule) => {
    const cfg = rule.config || {};
    setFormData({
      name: rule.name || '',
      rule_type: rule.rule_type || 'milestone',
      status: rule.status || 'active',
      reward_campaign_id: rule.reward_campaign_id != null ? String(rule.reward_campaign_id) : '',
      orders_required: cfg.orders_required != null ? String(cfg.orders_required) : '',
      reward_amount: cfg.reward_amount != null ? String(cfg.reward_amount) : '',
      trigger_category_id: cfg.trigger_category_id != null ? String(cfg.trigger_category_id) : '',
      trigger_category: cfg.trigger_category || '',
    });
  };

  // Always fetch the single-rule endpoint so the edit form reflects the latest server state.
  // The cached list row is used as an instant pre-fill so the form isn't blank during the round trip.
  const fetchRuleDetail = (id) => {
    if (!baseUrl || id == null) return Promise.resolve(null);
    setRuleDetailLoading(true);
    return simpleGetCallWithErrorResponse(`${baseUrl}/${id}`)
      .then((res) => {
        const json = res?.json;
        const data = json?.data ?? json;
        if (!res?.error && data && typeof data === 'object') {
          fillFormFromRule(data);
          return data;
        }
        ToastMsg('error', json?.message || 'Failed to load rule details.');
        return null;
      })
      .catch((err) => {
        console.error('Rule detail fetch error:', err);
        ToastMsg('error', 'Network error while loading rule details.');
        return null;
      })
      .finally(() => setRuleDetailLoading(false));
  };

  const openEdit = (rule) => {
    fillFormFromRule(rule);          // instant pre-fill from list row
    setEditingId(rule.id);
    setShowForm(true);
    fetchRuleDetail(rule.id);        // refresh with the freshest server data
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!baseUrl) {
      ToastMsg('error', 'Gamification API endpoint is not configured.');
      return;
    }
    const payload = {
      name: formData.name.trim(),
      rule_type: formData.rule_type,
      config: buildConfig(),
      reward_campaign_id: formData.reward_campaign_id ? Number(formData.reward_campaign_id) : null,
      status: formData.status,
    };

    const isEdit = editingId != null;
    const request = isEdit
      ? PutCallWithAuthToken(`${baseUrl}/${editingId}`, JSON.stringify(payload))
      : PostCallWithErrorResponse(baseUrl, JSON.stringify(payload));

    setSubmitting(true);
    request
      .then((res) => {
        if (!res?.error && (res?.json?.success || res?.json?.data)) {
          ToastMsg('success', res?.json?.message || (isEdit ? 'Rule updated' : 'Rule created'));
          cancelForm();
          fetchRules();
        } else {
          const errMsg = res?.json?.message
            || (Array.isArray(res?.json?.errors) ? res.json.errors.map((e) => e.msg).join(', ') : null)
            || (isEdit ? 'Failed to update rule' : 'Failed to create rule');
          ToastMsg('error', errMsg);
        }
      })
      .catch((err) => {
        console.error(isEdit ? 'Update rule error:' : 'Create rule error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const toggleStatus = (rule) => {
    if (!baseUrl) return;
    const next = rule.status === 'active' ? 'paused' : 'active';
    setBusyRuleId(rule.id);
    PutCallWithAuthToken(`${baseUrl}/${rule.id}`, JSON.stringify({ status: next }))
      .then((res) => {
        if (!res?.error && (res?.json?.success || res?.json?.data)) {
          ToastMsg('success', `Rule ${next === 'paused' ? 'paused' : 'resumed'}`);
          fetchRules();
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to update status');
        }
      })
      .catch(() => ToastMsg('error', 'Network error.'))
      .finally(() => setBusyRuleId(null));
  };

  // Open the confirmation modal — actual deletion happens in `confirmDeleteRule` below.
  const requestDeleteRule = (rule) => {
    if (!baseUrl) return;
    setDeleteTarget(rule);
  };

  const confirmDeleteRule = () => {
    const rule = deleteTarget;
    if (!rule || !baseUrl) return;
    setBusyRuleId(rule.id);
    DeleteCallWithErrorResponseWithTokenQuery(`${baseUrl}/${rule.id}`)
      .then((res) => {
        if (!res?.error) {
          ToastMsg('success', 'Rule deleted');
          fetchRules();
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to delete rule');
        }
      })
      .catch(() => ToastMsg('error', 'Network error.'))
      .finally(() => setBusyRuleId(null));
  };

  // ── Rendering helpers ────────────────────────────────────────────
  const ruleTypeMeta = (type) => RULE_TYPES.find((r) => r.value === type) || { label: type, icon: Sparkles, tint: { bg: '#f1f5f9', color: '#475569' }, description: '' };

  const renderConfigSummary = (rule) => {
    const cfg = rule.config || {};
    switch (rule.rule_type) {
      case 'milestone':
        return `Complete ${cfg.orders_required ?? '—'} orders → ₹${Number(cfg.reward_amount ?? 0).toLocaleString()} credit`;
      case 'first_order':
        return `₹${Number(cfg.reward_amount ?? 0).toLocaleString()} on first order`;
      case 'cross_sell':
        return `Order "${cfg.trigger_category || `#${cfg.trigger_category_id}` || '—'}" → ₹${Number(cfg.reward_amount ?? 0).toLocaleString()} credit`;
      case 'referral':
        return `₹${Number(cfg.reward_amount ?? 0).toLocaleString()} per successful referral`;
      default:
        return JSON.stringify(cfg);
    }
  };

  const StatusPill = ({ status }) => {
    const s = (status || '').toLowerCase();
    const map = {
      active:   { bg: '#dcfce7', color: '#15803d', dot: '#16a34a' },
      paused:   { bg: '#fef3c7', color: '#92400e', dot: '#d97706' },
      inactive: { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' },
    };
    const m = map[s] || map.inactive;
    return (
      <span style={pillStyle({ background: m.bg, color: m.color })}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: m.dot }}></span>
        {status || 'Unknown'}
      </span>
    );
  };

  // ── Form (inline panel) ─────────────────────────────────────────
  const renderForm = () => {
    const isEdit = editingId != null;
    const meta = ruleTypeMeta(formData.rule_type);
    const Icon = meta.icon;

    return (
      <form onSubmit={handleSubmit} className="shadow-sm border bg-white rounded-4" style={{ overflow: 'hidden' }}>
        <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <div style={{ background: meta.tint.bg, color: meta.tint.color, padding: 8, borderRadius: 8, display: 'flex' }}>
              <Icon size={18} />
            </div>
            <div>
              <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{isEdit ? 'Edit Rule' : 'New Gamification Rule'}</h6>
              <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>{meta.description}</p>
            </div>
          </div>
          {isEdit && (
            <div className="d-flex align-items-center gap-2">
              {ruleDetailLoading && (
                <span className="d-flex align-items-center gap-1 small" style={{ color: '#64748b' }}>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 12, height: 12 }}></span>
                  Refreshing…
                </span>
              )}
              <span style={pillStyle({ background: '#f1f5f9', color: '#64748b', fontSize: '0.7rem' })}>#{editingId}</span>
            </div>
          )}
        </div>

        {/* Rule type picker */}
        <div className="p-4 border-bottom">
          <div style={sectionTitle}>Rule Type</div>
          <div className="row g-2">
            {RULE_TYPES.map((t) => {
              const TIcon = t.icon;
              const selected = formData.rule_type === t.value;
              return (
                <div className="col-6 col-md-3" key={t.value}>
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, rule_type: t.value }))}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 10,
                      border: selected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                      background: selected ? '#eff6ff' : '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div style={{ background: t.tint.bg, color: t.tint.color, padding: 6, borderRadius: 6, display: 'flex' }}>
                      <TIcon size={16} />
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{t.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Common fields */}
        <div className="p-4 border-bottom">
          <div style={sectionTitle}>Basics</div>
          <div className="row g-3">
            <div className="col-md-6">
              <label style={fieldLabel}>Rule Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input required type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., 5-order milestone" style={inputBase} />
            </div>
            <div className="col-md-3">
              <label style={fieldLabel}>Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange} style={inputBase}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-3">
              <label style={fieldLabel}>Reward Campaign <span style={{ color: '#ef4444' }}>*</span></label>
              <select required name="reward_campaign_id" value={formData.reward_campaign_id} onChange={handleInputChange} style={inputBase}>
                <option value="">Select campaign…</option>
                {campaignOptions.map((c) => (
                  <option key={c.id} value={c.id}>#{c.id} — {c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Type-specific config */}
        <div className="p-4 border-bottom">
          <div style={sectionTitle}>Configuration</div>
          <div className="row g-3">
            {formData.rule_type === 'milestone' && (
              <div className="col-md-4">
                <label style={fieldLabel}>Orders Required <span style={{ color: '#ef4444' }}>*</span></label>
                <input required type="number" min="1" name="orders_required" value={formData.orders_required} onChange={handleInputChange} placeholder="5" style={inputBase} />
              </div>
            )}
            {formData.rule_type === 'cross_sell' && (
              <>
                <div className="col-md-4">
                  <label style={fieldLabel}>Trigger Category ID <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required type="number" min="1" name="trigger_category_id" value={formData.trigger_category_id} onChange={handleInputChange} placeholder="12" style={inputBase} />
                </div>
                <div className="col-md-4">
                  <label style={fieldLabel}>Trigger Category Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required type="text" name="trigger_category" value={formData.trigger_category} onChange={handleInputChange} placeholder="dessert" style={inputBase} />
                </div>
              </>
            )}
            <div className="col-md-4">
              <label style={fieldLabel}>Reward Amount <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '9px 12px', background: '#f8fafc', borderRight: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>₹</div>
                <input required type="number" min="0" step="0.01" name="reward_amount" value={formData.reward_amount} onChange={handleInputChange} placeholder="100" style={{ flex: 1, border: 'none', padding: '9px 12px', outline: 'none', background: 'transparent' }} />
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 px-4 d-flex justify-content-end gap-2" style={{ background: '#f8fafc' }}>
          <button type="button" onClick={cancelForm} disabled={submitting} className="btn btn-light fw-medium">Cancel</button>
          <button type="submit" disabled={submitting} className="btn btn-primary fw-medium px-4 d-flex align-items-center gap-2">
            {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14 }}></span>}
            {submitting ? (isEdit ? 'Updating…' : 'Saving…') : (isEdit ? 'Update Rule' : 'Create Rule')}
          </button>
        </div>
      </form>
    );
  };

  // ── Rule card ────────────────────────────────────────────────────
  const renderRuleCard = (rule) => {
    const meta = ruleTypeMeta(rule.rule_type);
    const Icon = meta.icon;
    const isBusy = busyRuleId === rule.id;
    const isActive = (rule.status || '').toLowerCase() === 'active';
    return (
      <div key={rule.id} className="col-md-6 col-xl-4">
        <div className="border bg-white rounded-4 shadow-sm h-100 d-flex flex-column">
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
              <div style={{ background: meta.tint.bg, color: meta.tint.color, padding: 8, borderRadius: 8, display: 'flex', flexShrink: 0 }}>
                <Icon size={18} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h6 className="fw-semibold mb-0 text-truncate" style={{ color: 'var(--text-primary)' }}>{rule.name}</h6>
                <span style={pillStyle({ background: meta.tint.bg, color: meta.tint.color, fontSize: '0.65rem', marginTop: 2 })}>{meta.label}</span>
              </div>
            </div>
            <StatusPill status={rule.status} />
          </div>
          <div className="p-3 flex-grow-1 d-flex flex-column gap-2">
            <p className="small mb-0" style={{ color: '#475569', fontSize: '0.82rem' }}>{renderConfigSummary(rule)}</p>
            <div className="d-flex flex-wrap gap-3 mt-1">
              {rule.reward_campaign_id != null && (
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Campaign</div>
                  <div style={{ fontSize: '0.78rem', color: '#1e293b' }}>#{rule.reward_campaign_id}</div>
                </div>
              )}
              {rule.id != null && (
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rule ID</div>
                  <div style={{ fontSize: '0.78rem', color: '#1e293b' }}>#{rule.id}</div>
                </div>
              )}
            </div>
          </div>
          <div className="p-3 border-top d-flex gap-2" style={{ background: '#f8fafc' }}>
            <button type="button" disabled={isBusy} onClick={() => openEdit(rule)} className="btn btn-sm btn-outline-primary fw-medium flex-grow-1">Edit</button>
            <button type="button" disabled={isBusy} onClick={() => toggleStatus(rule)} className={`btn btn-sm fw-medium flex-grow-1 ${isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}>
              {isBusy ? '…' : (isActive ? 'Pause' : 'Resume')}
            </button>
            <button type="button" disabled={isBusy} onClick={() => requestDeleteRule(rule)} className="btn btn-sm btn-outline-danger fw-medium" title="Delete">
              <XCircle size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Gamification & Behavioral Challenges</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>
            Set up habit-forming mechanics that reward specific customer behaviors.
            {!rulesLoading && (
              <span className="ms-2" style={pillStyle({ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' })}>{rules.length} rule{rules.length === 1 ? '' : 's'}</span>
            )}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label className="d-flex align-items-center gap-2 small" style={{ color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
            Active only
          </label>
          {!showForm && (
            <button type="button" onClick={openCreate} className="btn btn-primary d-flex align-items-center gap-2 fw-medium">
              <Plus size={16} /> New Challenge
            </button>
          )}
        </div>
      </div>

      {showForm && renderForm()}

      <div className="row g-3">
        {rulesLoading && (
          <div className="col-12 text-center py-5" style={{ color: 'var(--text-secondary)' }}>Loading rules…</div>
        )}
        {!rulesLoading && rules.length === 0 && (
          <div className="col-12">
            <div className="border bg-white rounded-4 p-5 text-center" style={{ borderStyle: 'dashed' }}>
              <Sparkles size={28} className="text-secondary mb-2" />
              <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>No gamification rules yet</h6>
              <p className="small mb-3" style={{ color: 'var(--text-secondary)' }}>Create your first challenge to start rewarding customer behavior.</p>
              {!showForm && (
                <button type="button" onClick={openCreate} className="btn btn-primary d-inline-flex align-items-center gap-2 fw-medium">
                  <Plus size={16} /> New Challenge
                </button>
              )}
            </div>
          </div>
        )}
        {!rulesLoading && rules.map(renderRuleCard)}
      </div>

      <DeleteModal
        show={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDelete={confirmDeleteRule}
        message={deleteTarget ? `Are you sure you want to delete "${deleteTarget.name}"? This cannot be undone.` : ''}
      />

      {/* Referrals management — separate panel, self-contained */}
      {/* <ReferralsPanel /> */}
    </div>
  );
}

// ─── Referrals Panel (used by GamificationEngine) ────────────────
const REFERRAL_STATUS_TABS = [
  { value: 'pending',   label: 'Pending'   },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '',          label: 'All'       },
];

function ReferralsPanel() {
  const { ApiConfig } = useContext(AppContext);
  const baseUrl = ApiConfig?.GET_WALLET_ADMIN_REFERRALS;

  const [statusFilter, setStatusFilter] = useState('pending');
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);

  // Search-by-referee
  const [refereeSearch, setRefereeSearch] = useState('');
  const [refereeResult, setRefereeResult] = useState(null);
  const [refereeLoading, setRefereeLoading] = useState(false);

  // Cancel confirmation
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelBusyId, setCancelBusyId] = useState(null);

  const pillStyle = (extra = {}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.72rem',
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    ...extra,
  });

  const STATUS_PILL = {
    pending:   { bg: '#fef3c7', color: '#92400e', dot: '#d97706' },
    completed: { bg: '#dcfce7', color: '#15803d', dot: '#16a34a' },
    cancelled: { bg: '#fee2e2', color: '#991b1b', dot: '#dc2626' },
    expired:   { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' },
  };

  const statusPill = (status) => {
    const s = (status || '').toLowerCase();
    const m = STATUS_PILL[s] || { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
    return (
      <span style={pillStyle({ background: m.bg, color: m.color })}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: m.dot }}></span>
        {status || 'unknown'}
      </span>
    );
  };

  // ── API ──────────────────────────────────────────────────────────
  const fetchReferrals = () => {
    if (!baseUrl) return;
    setReferralsLoading(true);
    const qs = new URLSearchParams({ limit: '100', offset: '0' });
    if (statusFilter) qs.set('status', statusFilter);
    simpleGetCallWithErrorResponse(`${baseUrl}?${qs.toString()}`)
      .then((res) => {
        console.log('Referrals list response:', res?.json);
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data
                   : Array.isArray(json?.data?.referrals) ? json.data.referrals
                   : Array.isArray(json) ? json
                   : [];
        setReferrals(list);
      })
      .catch((err) => {
        console.error('Referrals fetch error:', err);
        ToastMsg('error', 'Failed to load referrals.');
      })
      .finally(() => setReferralsLoading(false));
  };

  const fetchByReferee = () => {
    const id = refereeSearch.trim();
    if (!baseUrl || !id) return;
    setRefereeLoading(true);
    setRefereeResult(null);
    simpleGetCallWithErrorResponse(`${baseUrl}/referee/${encodeURIComponent(id)}`)
      .then((res) => {
        console.log('Referrals by referee response:', res?.json);
        const json = res?.json;
        const data = json?.data ?? json;
        setRefereeResult(data);
      })
      .catch((err) => {
        console.error('Referee lookup error:', err);
        ToastMsg('error', 'Lookup failed.');
      })
      .finally(() => setRefereeLoading(false));
  };

  const requestCancel = (ref) => setCancelTarget(ref);

  const confirmCancel = () => {
    const ref = cancelTarget;
    if (!ref || !baseUrl) return;
    const id = ref.id ?? ref.referral_id;
    if (id == null) {
      ToastMsg('error', 'Referral id missing — cannot cancel.');
      return;
    }
    setCancelBusyId(id);
    PostCallWithErrorResponse(`${baseUrl}/${id}/cancel`, JSON.stringify({}))
      .then((res) => {
        if (!res?.error && (res?.json?.success || res?.json?.data || res?.response?.ok)) {
          ToastMsg('success', res?.json?.message || 'Referral cancelled');
          fetchReferrals();
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to cancel referral');
        }
      })
      .catch(() => ToastMsg('error', 'Network error.'))
      .finally(() => setCancelBusyId(null));
  };

  useEffect(() => {
    // fetchReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, statusFilter]);

  // ── Best-guess accessors (response shape will be confirmed later) ──
  const referralName = (r) => r?.referee_name || r?.referee?.name || r?.name || '—';
  const referrerName = (r) => r?.referrer_name || r?.referrer?.name || '—';
  const refereeId = (r) => r?.referee_id ?? r?.referee?.id ?? '—';
  const referrerId = (r) => r?.referrer_id ?? r?.referrer?.id ?? '—';
  const referralCreatedAt = (r) => r?.created_at || r?.createdAt || r?.created || null;
  const referralStatus = (r) => r?.status || '';
  const referralRewardAmount = (r) => r?.reward_amount ?? r?.config?.reward_amount ?? null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const inr = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="border bg-white rounded-4 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <div style={{ background: '#ede9fe', color: '#6d28d9', padding: 8, borderRadius: 8, display: 'flex' }}>
            <Share2 size={18} />
          </div>
          <div>
            <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Refer & Earn — Referrals</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Track and manage individual referral records.</p>
          </div>
        </div>
        {!referralsLoading && (
          <span style={pillStyle({ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' })}>
            {referrals.length} record{referrals.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Filter / search bar */}
      <div className="px-4 py-3 border-bottom d-flex flex-wrap gap-3 align-items-center" style={{ background: '#f8fafc' }}>
        <div className="btn-group" role="group">
          {REFERRAL_STATUS_TABS.map((t) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => setStatusFilter(t.value)}
              className={`btn btn-sm fw-medium px-3 ${statusFilter === t.value ? 'btn-primary' : 'btn-outline-secondary bg-white'}`}
              style={{ fontSize: '0.75rem' }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <button type="button" onClick={fetchReferrals} disabled={referralsLoading} className="btn btn-sm btn-light border fw-medium px-3" style={{ fontSize: '0.75rem' }}>
          {referralsLoading ? 'Refreshing…' : 'Refresh'}
        </button>

        <div className="ms-auto d-flex align-items-center gap-2">
          <input
            type="text"
            placeholder="Search by referee ID…"
            value={refereeSearch}
            onChange={(e) => setRefereeSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchByReferee()}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.8rem', width: 220, outline: 'none', background: '#fff' }}
          />
          <button type="button" onClick={fetchByReferee} disabled={!refereeSearch.trim() || refereeLoading} className="btn btn-sm btn-outline-primary fw-medium" style={{ fontSize: '0.75rem' }}>
            {refereeLoading ? 'Looking up…' : 'Lookup'}
          </button>
          {refereeResult != null && (
            <button type="button" onClick={() => { setRefereeResult(null); setRefereeSearch(''); }} className="btn btn-sm btn-link text-secondary p-0" style={{ fontSize: '0.75rem' }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Referee-lookup result banner */}
      {refereeResult != null && (
        <div className="px-4 py-3 border-bottom" style={{ background: '#eff6ff' }}>
          <div className="small fw-semibold mb-1" style={{ color: '#1e40af' }}>
            Lookup result for referee #{refereeSearch}
          </div>
          <div className="small" style={{ color: '#1e3a8a', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {(() => {
              try { return JSON.stringify(refereeResult, null, 2); }
              catch { return String(refereeResult); }
            })()}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead style={{ background: '#f8fafc' }}>
            <tr style={{ fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>
              <th className="ps-4 py-3" style={{ width: 60 }}>#</th>
              <th className="py-3">Referrer</th>
              <th className="py-3">Referee</th>
              <th className="py-3">Reward</th>
              <th className="py-3">Status</th>
              <th className="py-3">Created</th>
              <th className="py-3 pe-4 text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {referralsLoading && (
              <tr><td colSpan="7" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>Loading referrals…</td></tr>
            )}
            {!referralsLoading && referrals.length === 0 && (
              <tr><td colSpan="7" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>
                {statusFilter ? `No ${statusFilter} referrals.` : 'No referrals yet.'}
              </td></tr>
            )}
            {!referralsLoading && referrals.map((r, i) => {
              const id = r.id ?? r.referral_id ?? i;
              const status = referralStatus(r);
              const isPending = (status || '').toLowerCase() === 'pending';
              const isBusy = cancelBusyId === id;
              return (
                <tr key={id}>
                  <td className="ps-4 small" style={{ color: 'var(--text-secondary)' }}>{i + 1}</td>
                  <td>
                    <div className="fw-semibold" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{referrerName(r)}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>#{referrerId(r)}</div>
                  </td>
                  <td>
                    <div className="fw-medium" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{referralName(r)}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>#{refereeId(r)}</div>
                  </td>
                  <td className="fw-medium" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{inr(referralRewardAmount(r))}</td>
                  <td>{statusPill(status)}</td>
                  <td className="small" style={{ color: 'var(--text-secondary)' }}>{fmtDate(referralCreatedAt(r))}</td>
                  <td className="text-end pe-4">
                    {isPending ? (
                      <button type="button" disabled={isBusy} onClick={() => requestCancel(r)} className="btn btn-sm btn-outline-danger fw-medium" style={{ fontSize: '0.72rem' }}>
                        {isBusy ? 'Cancelling…' : 'Cancel'}
                      </button>
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DeleteModal
        show={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onDelete={confirmCancel}
        message={cancelTarget ? `Cancel referral #${cancelTarget.id ?? cancelTarget.referral_id ?? ''}? This cannot be undone.` : ''}
      />
    </div>
  );
}

// ─── Screen 4: Permissions ────────────────────────────────────────
function PermissionsControl() {
  const { ApiConfig, userData } = useContext(AppContext);
  const campaignsBase = ApiConfig?.GET_WALLET_ADMIN_CAMPAIGNS;

  // ── Manual Credit form state ─────────────────────────────────────
  const initialForm = {
    campaign_id: '',
    user_id: '',
    fullName: '',
    email: '',
    phone: '',
    amount: '',
    description: '',
  };
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  // Add these new states for the User Dropdown
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Add this useEffect to fetch users
  useEffect(() => {
    setUsersLoading(true);
    simpleGetCallWithErrorResponse(`${ApiConfig.GET_MARK_USER_LIST}?type=All&page_size=999`)
      .then((res) => {
        const json = res?.json;
        // Adjust this depending on your API's exact response structure (e.g., json.data, json.users, etc.)
        const list = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        setUsersList(list);
      })
      .catch((err) => console.error('Users fetch error:', err))
      .finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    if (!campaignsBase) return;
    setCampaignsLoading(true);
    simpleGetCallWithErrorResponse(`${campaignsBase}?limit=100&offset=0`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data
                   : Array.isArray(json?.data?.campaigns) ? json.data.campaigns
                   : Array.isArray(json) ? json : [];
        setCampaignOptions(list.map((c) => ({ id: c.id, name: c.name, status: c.status })));
      })
      .catch((err) => console.error('Campaigns fetch error:', err))
      .finally(() => setCampaignsLoading(false));
  }, [campaignsBase]);

  // ── Validation ────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.campaign_id) e.campaign_id = 'Campaign is required.';
    if (!form.user_id) e.user_id = 'User ID is required.';
    else if (!/^\d+$/.test(String(form.user_id).trim())) e.user_id = 'User ID must be a positive integer.';
    if (!form.fullName.trim()) e.fullName = 'Full name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address.';
    if (!form.phone.trim()) e.phone = 'Phone is required.';
    else if (!/^\+?\d{10,15}$/.test(form.phone.trim().replace(/\s|-/g, ''))) e.phone = 'Phone must be 10–15 digits, optionally with +.';
    if (form.amount === '' || form.amount == null) e.amount = 'Amount is required.';
    else if (Number(form.amount) <= 0) e.amount = 'Amount must be greater than 0.';
    if (!form.description.trim()) e.description = 'Description is required for audit.';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) return;
    if (!campaignsBase) {
      ToastMsg('error', 'Wallet API endpoint is not configured.');
      return;
    }

    const issuedBy = userData?.Username || userData?.FirstName || localStorage.getItem('Username') || 'admin';
    const payload = {
      user_id: Number(form.user_id),
      amount: Number(form.amount),
      description: form.description.trim(),
      metadata: { issued_by: issuedBy },
      user_profile: {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      },
    };

    setSubmitting(true);
    PostCallWithErrorResponse(`${campaignsBase}/${form.campaign_id}/credit-manual`, JSON.stringify(payload))
      .then((res) => {
        if (!res?.error && (res?.json?.success || res?.json?.data)) {
          ToastMsg('success', res?.json?.message || 'Credit issued successfully');
          setForm(initialForm);
          setErrors({});
        } else {
          const msg = res?.json?.message
            || (Array.isArray(res?.json?.errors) ? res.json.errors.map((x) => x.msg).join(', ') : null)
            || 'Failed to issue credit';
          ToastMsg('error', msg);
        }
      })
      .catch((err) => {
        console.error('Manual credit error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  // ── Inline-styled inputs (bypasses bundle.css's broken .input-group) ──
  const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: 6, display: 'block', letterSpacing: '0.01em' };
  const errorText = { fontSize: '0.7rem', color: '#dc2626', marginTop: 4, display: 'block' };
  const inputBase = (hasError) => ({
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: `1px solid ${hasError ? '#fca5a5' : '#e2e8f0'}`,
    background: '#fff',
    fontSize: '0.875rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
  });
  const prefixWrap = (hasError) => ({
    display: 'flex',
    alignItems: 'stretch',
    border: `1px solid ${hasError ? '#fca5a5' : '#e2e8f0'}`,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
  });
  const prefixBox = { padding: '9px 12px', background: '#f8fafc', borderRight: '1px solid #e2e8f0', color: '#475569', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center' };
  const prefixInput = { flex: 1, border: 'none', padding: '9px 12px', fontSize: '0.875rem', color: '#1e293b', outline: 'none', background: 'transparent' };

  return (
    <div className="row g-4">
      <div className="col-12">
        <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Staff Distribution & Permissions</h5>
        <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Control who can manually issue promotional credits and set hard caps.</p>
      </div>

      {/* Role-based limits table — unchanged */}
      <div className="col-lg-7 d-flex flex-column gap-4">
        <div className="data-table-wrapper">
          <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
            <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <ShieldCheck size={16} className="text-primary" /> Role-Based Credit Limits
            </h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>System Role</th>
                  <th>Max Issue Cap</th>
                  <th>Approval Required</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-secondary-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><Users size={14} className="text-secondary"/></div>
                    Call Center Agent
                  </td>
                  <td><span className="border border-secondary text-secondary bg-light px-2 py-1 fw-medium" style={{ borderRadius: 6, fontSize: '0.72rem' }}>5% Discount</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>No</td>
                </tr>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-primary-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><ShieldCheck size={14} className="text-primary"/></div>
                    Shift Manager
                  </td>
                  <td><span className="border border-primary-subtle text-primary bg-primary-subtle px-2 py-1 fw-medium" style={{ borderRadius: 6, fontSize: '0.72rem' }}>10% Discount</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>No</td>
                </tr>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-info-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><AlertCircle size={14} className="text-info-emphasis"/></div>
                    Administrator
                  </td>
                  <td><span className="border border-info-subtle text-info-emphasis bg-info-subtle px-2 py-1 fw-medium" style={{ borderRadius: 6, fontSize: '0.72rem' }}>Unlimited (Override)</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Manual Credit Push form */}
      <div className="col-lg-5">
        <form onSubmit={handleSubmit} className="border bg-white rounded-4 shadow-sm" style={{ overflow: 'hidden' }}>
          <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2">
            <div style={{ background: '#dcfce7', color: '#15803d', padding: 8, borderRadius: 8, display: 'flex' }}>
              <Gift size={18} />
            </div>
            <div>
              <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Manual Credit Push</h6>
              <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Issue a one-off wallet credit against a campaign.</p>
            </div>
          </div>

          <div className="p-4 d-flex flex-column gap-3">
            {/* Campaign */}
            <div>
              <label style={fieldLabel}>Campaign <span style={{ color: '#ef4444' }}>*</span></label>
              <select name="campaign_id" value={form.campaign_id} onChange={handleChange} style={inputBase(!!errors.campaign_id)} disabled={campaignsLoading}>
                <option value="">{campaignsLoading ? 'Loading campaigns…' : 'Select a campaign…'}</option>
                {campaignOptions.map((c) => (
                  <option key={c.id} value={c.id}>#{c.id} — {c.name}{c.status && c.status !== 'active' ? ` (${c.status})` : ''}</option>
                ))}
              </select>
              {errors.campaign_id && <span style={errorText}>{errors.campaign_id}</span>}
            </div>

            {/* User ID + Amount */}
            <div className="row g-3">
          <div className="col-6">
                <label style={fieldLabel}>Select User <span style={{ color: '#ef4444' }}>*</span></label>
                <select 
                  name="user_id" 
                  value={form.user_id} 
                  onChange={handleChange} 
                  style={inputBase(!!errors.user_id)} 
                  disabled={usersLoading}
                >
                  <option value="">{usersLoading ? 'Loading users...' : 'Select a user...'}</option>
                  {usersList.map((user) => (
                    // Make sure 'user.id' and 'user.name' match the exact keys returned by your API
                    <option key={user.id} value={user.id}>
                      {user.first_name ? `${user.first_name} (#${user.id})` : `User #${user.id}`}
                    </option>
                  ))}
                </select>
                {errors.user_id && <span style={errorText}>{errors.user_id}</span>}
              </div>
              <div className="col-6">
                <label style={fieldLabel}>Amount <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={prefixWrap(!!errors.amount)}>
                  <div style={prefixBox}>₹</div>
                  <input type="number" min="0" step="0.01" name="amount" value={form.amount} onChange={handleChange} placeholder="Enter amount" style={prefixInput} />
                </div>
                {errors.amount && <span style={errorText}>{errors.amount}</span>}
              </div>
            </div>

            {/* User profile */}
            <div className="border-top pt-3">
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Customer Profile</div>
              <div className="d-flex flex-column gap-3">
                <div>
                  <label style={fieldLabel}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" name="fullName" value={form.fullName} onChange={handleChange} placeholder="Enter full name" style={inputBase(!!errors.fullName)} />
                  {errors.fullName && <span style={errorText}>{errors.fullName}</span>}
                </div>
                <div className="row g-3">
                  <div className="col-7">
                    <label style={fieldLabel}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="name@example.com" style={inputBase(!!errors.email)} />
                    {errors.email && <span style={errorText}>{errors.email}</span>}
                  </div>
                  <div className="col-5">
                    <label style={fieldLabel}>Phone <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" style={inputBase(!!errors.phone)} />
                    {errors.phone && <span style={errorText}>{errors.phone}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="border-top pt-3">
              <label style={fieldLabel}>Description <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea name="description" rows={2} value={form.description} onChange={handleChange} placeholder="One-off goodwill credit — late delivery complaint…" style={{ ...inputBase(!!errors.description), resize: 'vertical', minHeight: 60 }} />
              {errors.description && <span style={errorText}>{errors.description}</span>}
            </div>
          </div>

          <div className="px-4 py-3 border-top d-flex align-items-center justify-content-end gap-2" style={{ background: '#f8fafc' }}>
            {/* <span className="small" style={{ color: '#94a3b8', fontSize: '0.72rem' }}>Issued by <strong>{userData?.Username || userData?.FirstName || 'admin'}</strong></span> */}
            <button type="submit" disabled={submitting} className="btn btn-dark fw-medium px-4 d-flex align-items-center gap-2">
              {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14 }}></span>}
              {submitting ? 'Issuing…' : 'Issue Credit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Screen 5: Strategic Reporting ───────────────────────────────
// Source-type → display config (used in the breakdown chart and rows table).
// New source types from the API will fall back to a neutral palette automatically.
const SOURCE_META = {
  MANUAL:      { label: 'Manual Credits',     color: '#64748b', accent: 'bg-secondary' },
  CAMPAIGN:    { label: 'Campaigns',          color: '#2563eb', accent: 'bg-primary' },
  GAMIFICATION:{ label: 'Gamification',       color: '#a855f7', accent: '' },
  REFERRAL:    { label: 'Referrals',          color: '#0ea5e9', accent: 'bg-info' },
  WORKFLOW:    { label: 'Automated Workflows',color: '#16a34a', accent: 'bg-success' },
  REFUND:      { label: 'Refund Reversals',   color: '#f97316', accent: 'bg-warning' },
};
const FALLBACK_SOURCE_META = { label: 'Other', color: '#94a3b8', accent: 'bg-secondary' };

const RANGE_PRESETS = [
  { key: '7d',  label: 'Last 7 Days',  days: 7  },
  { key: '30d', label: 'Last 30 Days', days: 30 },
  { key: '90d', label: 'Last 90 Days', days: 90 },
];

function StrategicReporting() {
  const { ApiConfig } = useContext(AppContext);
  const reportUrl = ApiConfig?.GET_WALLET_ADMIN_REPORTS_OUTCOME;

  const buildRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    return { from: from.toISOString(), to: to.toISOString() };
  };

  const [presetKey, setPresetKey] = useState('30d');
  const [range, setRange] = useState(() => buildRange(30));
  const [outcomeReport, setOutcomeReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchReport = (r = range) => {
    if (!reportUrl) return;
    setReportLoading(true);
    const url = `${reportUrl}?from=${encodeURIComponent(r.from)}&to=${encodeURIComponent(r.to)}`;
    simpleGetCallWithErrorResponse(url)
      .then((res) => {
        const json = res?.json;
        const data = json?.data ?? json;
        setOutcomeReport(data);
      })
      .catch((err) => {
        console.error('Strategic outcome report fetch error:', err);
        ToastMsg('error', 'Failed to load strategic outcome report.');
      })
      .finally(() => setReportLoading(false));
  };

  useEffect(() => {
    fetchReport(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportUrl, range.from, range.to]);

  const applyPreset = (key) => {
    const preset = RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    setPresetKey(key);
    setRange(buildRange(preset.days));
  };

  // ── Derive view-model from response ──────────────────────────────
  const totals = outcomeReport?.totals || {};
  const rows = Array.isArray(outcomeReport?.rows) ? outcomeReport.rows : [];

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const num = (n) => Number(n || 0);
  const pct = (numer, denom) => {
    const d = num(denom);
    if (!d) return 0;
    return Math.round((num(numer) / d) * 100);
  };
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
  };

  // Aggregate rows by source_type → for the breakdown chart on the left
  const sourceBreakdown = useMemo(() => {
    const acc = new Map();
    rows.forEach((r) => {
      const key = r.source_type || 'OTHER';
      const cur = acc.get(key) || { source_type: key, issued: 0, consumed: 0, expired: 0, refund_reversed: 0, net_consumed: 0, lot_count: 0 };
      cur.issued += num(r.issued);
      cur.consumed += num(r.consumed);
      cur.expired += num(r.expired);
      cur.refund_reversed += num(r.refund_reversed);
      cur.net_consumed += num(r.net_consumed);
      cur.lot_count += num(r.lot_count);
      acc.set(key, cur);
    });
    const totalIssued = Array.from(acc.values()).reduce((s, b) => s + b.issued, 0);
    return Array.from(acc.values())
      .map((b) => ({ ...b, share: totalIssued ? Math.round((b.issued / totalIssued) * 100) : 0 }))
      .sort((a, b) => b.issued - a.issued);
  }, [rows]);

  const sourceMeta = (key) => SOURCE_META[key] || FALLBACK_SOURCE_META;

  // KPI tiles driven entirely by `totals`
  const redemptionRate = pct(totals.consumed, totals.issued);
  const expiredRate = pct(totals.expired, totals.issued);
  const liability = num(totals.issued) - num(totals.consumed) - num(totals.expired) - num(totals.refund_reversed);

  // Period summary copy (replaces the dummy AI insight)
  const periodSummary = (() => {
    if (!outcomeReport) return 'Loading the latest outcome data…';
    const issued = num(totals.issued);
    if (issued === 0) return 'No wallet credits were issued in this window. Pick a longer range or launch a campaign to see results.';
    const parts = [];
    parts.push(`Issued ${inr(issued)} across ${rows.length} source${rows.length === 1 ? '' : 's'}.`);
    if (num(totals.consumed) > 0) parts.push(`${redemptionRate}% redeemed.`);
    else parts.push(`No redemptions yet — promotional liability sits at ${inr(liability)}.`);
    if (num(totals.expired) > 0) parts.push(`${inr(totals.expired)} expired (saved cost).`);
    if (num(totals.refund_reversed) > 0) parts.push(`${inr(totals.refund_reversed)} refunded/reversed.`);
    return parts.join(' ');
  })();

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="d-flex flex-column gap-4">
      {/* Header */}
      <div className="row g-4">
        <div className="col-lg-5">
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Strategic Outcome Report</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Measure actual ROI and strategic success of wallet spend.</p>
          <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
            <div className="btn-group" role="group">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className={`btn btn-sm fw-medium px-3 ${presetKey === p.key ? 'btn-primary' : 'btn-outline-secondary bg-white'}`}
                  style={{ fontSize: '0.75rem' }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => fetchReport(range)} disabled={reportLoading} className="btn btn-sm btn-light border fw-medium px-3" style={{ fontSize: '0.75rem' }}>
              {reportLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button type="button" disabled className="btn btn-sm btn-outline-primary fw-medium px-3" style={{ fontSize: '0.75rem' }} title="Export endpoint not configured">Export</button>
          </div>
          <div className="mt-2 small" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
            <Calendar size={12} className="me-1" />
            {fmtDate(outcomeReport?.from || range.from)} → {fmtDate(outcomeReport?.to || range.to)}
          </div>
        </div>

        {/* Period summary card (replaces dummy AI insight) */}
        <div className="col-lg-7">
          <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-4 shadow-sm position-relative overflow-hidden h-100">
            <div className="position-absolute top-0 end-0 bg-primary opacity-10 rounded-circle" style={{ width: 150, height: 150, filter: 'blur(30px)', marginRight: -30, marginTop: -30 }}></div>
            <div className="d-flex gap-3 position-relative" style={{ zIndex: 1 }}>
              <div className="bg-primary text-white p-2 rounded-3 align-self-start"><Sparkles size={20} /></div>
              <div className="flex-grow-1">
                <h6 className="small fw-bold text-primary-emphasis mb-1">Period Summary</h6>
                <p className="small text-primary-emphasis mb-2 lh-base">{periodSummary}</p>
                <div className="d-flex flex-wrap gap-3 mt-2">
                  <div>
                    <div className="text-uppercase fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.05em', color: '#1e3a8a', opacity: 0.7 }}>Open Liability</div>
                    <div className="fw-bold text-primary-emphasis" style={{ fontSize: '0.95rem' }}>{inr(liability)}</div>
                  </div>
                  <div>
                    <div className="text-uppercase fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.05em', color: '#1e3a8a', opacity: 0.7 }}>Sources</div>
                    <div className="fw-bold text-primary-emphasis" style={{ fontSize: '0.95rem' }}>{rows.length}</div>
                  </div>
                  <div>
                    <div className="text-uppercase fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.05em', color: '#1e3a8a', opacity: 0.7 }}>Redemption</div>
                    <div className="fw-bold text-primary-emphasis" style={{ fontSize: '0.95rem' }}>{redemptionRate}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI tiles — driven by totals */}
      <div className="row g-3">
        {[
          { label: 'Total Issued',     value: inr(totals.issued),          sub: `${rows.length} source${rows.length === 1 ? '' : 's'}`, theme: 'bg-white',                                       icon: <IndianRupee size={16} className="text-secondary" />, dark: false },
          { label: 'Net Consumed',     value: inr(totals.net_consumed),    sub: `${redemptionRate}% Redemption`,                        theme: 'bg-success-subtle border border-success-subtle', icon: <CheckCircle2 size={16} className="text-success" />,  dark: false },
          { label: 'Expired (Saved)',  value: inr(totals.expired),         sub: expiredRate ? `${expiredRate}% of issued` : 'No expiry yet', theme: 'bg-white border border-secondary-subtle',     icon: <Clock size={16} className="text-secondary" />,       dark: false },
          { label: 'Refund Reversed',  value: inr(totals.refund_reversed), sub: 'Returned to wallet',                                   theme: 'bg-dark',                                        icon: <TrendingUp size={16} className="text-light opacity-75" />, dark: true },
        ].map(({ label, value, sub, theme, icon, dark }) => (
          <div className="col-6 col-md-3" key={label}>
            <div className={`card border-0 shadow-sm rounded-4 p-3 ${theme}`}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className={`fw-bold text-uppercase ${dark ? 'text-light opacity-75' : 'text-secondary'}`} style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>{label}</span>
                {icon}
              </div>
              <h3 className={`fs-3 fw-bold mb-2 ${dark ? 'text-white' : ''}`} style={dark ? {} : { color: 'var(--text-primary)' }}>{reportLoading ? '…' : value}</h3>
              <span className={`fw-medium align-self-start px-2 py-1 rounded ${dark ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '0.7rem', background: dark ? 'rgba(255,255,255,0.12)' : '#f1f5f9', border: dark ? 'none' : '1px solid #e2e8f0' }}>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        {/* Source breakdown — replaces dummy "Wallet Spend by Type" */}
        <div className="col-lg-5">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
            <h6 className="fw-semibold mb-4 d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <PieChart size={16} className="text-secondary"/> Issued by Source
            </h6>

            {sourceBreakdown.length === 0 ? (
              <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                <PieChart size={28} className="mb-2 opacity-50" />
                <div className="small">No issuance recorded in this window.</div>
              </div>
            ) : (
              <>
                <div className="progress mb-4 rounded-3 shadow-sm" style={{ height: '2rem', backgroundColor: '#e2e8f0' }}>
                  {sourceBreakdown.map((b) => (
                    <div key={b.source_type} className="progress-bar" style={{ width: `${b.share}%`, backgroundColor: sourceMeta(b.source_type).color }} title={`${sourceMeta(b.source_type).label}: ${inr(b.issued)} (${b.share}%)`}></div>
                  ))}
                </div>
                <div className="d-flex flex-column gap-2 flex-grow-1">
                  {sourceBreakdown.map((b) => {
                    const meta = sourceMeta(b.source_type);
                    return (
                      <div className="d-flex align-items-center justify-content-between small" key={b.source_type}>
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                          <div className="rounded-1 flex-shrink-0" style={{ width: 12, height: 12, backgroundColor: meta.color }}></div>
                          <span className="fw-medium text-truncate" style={{ color: 'var(--text-primary)' }}>{meta.label}</span>
                        </div>
                        <div className="d-flex align-items-center gap-3 text-end flex-shrink-0">
                          <span style={{ color: 'var(--text-secondary)', width: 36 }}>{b.share}%</span>
                          <span className="fw-semibold" style={{ color: 'var(--text-primary)', minWidth: 70 }}>{inr(b.issued)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pt-3 mt-3 border-top d-flex justify-content-between align-items-end">
                  <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Total Issued</span>
                  <span className="fs-4 fw-bold" style={{ color: 'var(--text-primary)' }}>{inr(totals.issued)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Detailed rows table — replaces dummy goals quadrant */}
        <div className="col-lg-7">
          <div className="card shadow-sm border-0 rounded-4 h-100 d-flex flex-column overflow-hidden">
            <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between">
              <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Workflow size={16} className="text-secondary" /> Per-Source Breakdown
              </h6>
              {!reportLoading && (
                <span className="small" style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>{rows.length} row{rows.length === 1 ? '' : 's'}</span>
              )}
            </div>
            <div className="table-responsive flex-grow-1">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: '#f8fafc' }}>
                  <tr style={{ fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>
                    <th className="ps-4 py-3">Source</th>
                    <th className="py-3">Campaign</th>
                    <th className="py-3 text-end">Issued</th>
                    <th className="py-3 text-end">Consumed</th>
                    <th className="py-3 text-end">Expired</th>
                    <th className="py-3 text-end">Refunded</th>
                    <th className="py-3 text-end">Net</th>
                    <th className="py-3 pe-4 text-end">Lots</th>
                  </tr>
                </thead>
                <tbody>
                  {reportLoading && (
                    <tr><td colSpan="8" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>Loading…</td></tr>
                  )}
                  {!reportLoading && rows.length === 0 && (
                    <tr><td colSpan="8" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>No activity in this window.</td></tr>
                  )}
                  {!reportLoading && rows.map((r, i) => {
                    const meta = sourceMeta(r.source_type);
                    return (
                      <tr key={`${r.source_type}-${r.source_id}-${i}`}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: meta.color }}></span>
                            <div>
                              <div className="fw-semibold" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{meta.label}</div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>#{r.source_id ?? '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.campaign_name ? (
                            <div>
                              <div className="fw-medium" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{r.campaign_name}</div>
                              <div className="d-flex align-items-center gap-2 mt-1">
                                {r.campaign_type && (
                                  <span className="text-capitalize px-2 py-0 rounded" style={{ fontSize: '0.65rem', background: '#eff6ff', color: '#1e40af' }}>{r.campaign_type}</span>
                                )}
                                {r.campaign_status && (
                                  <span className="text-capitalize px-2 py-0 rounded" style={{ fontSize: '0.65rem', background: r.campaign_status === 'active' ? '#dcfce7' : '#f1f5f9', color: r.campaign_status === 'active' ? '#15803d' : '#475569' }}>{r.campaign_status}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>—</span>
                          )}
                        </td>
                        <td className="text-end fw-semibold" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{inr(r.issued)}</td>
                        <td className="text-end" style={{ color: '#15803d', fontSize: '0.82rem' }}>{inr(r.consumed)}</td>
                        <td className="text-end" style={{ color: '#92400e', fontSize: '0.82rem' }}>{inr(r.expired)}</td>
                        <td className="text-end" style={{ color: '#b45309', fontSize: '0.82rem' }}>{inr(r.refund_reversed)}</td>
                        <td className="text-end fw-semibold" style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>{inr(r.net_consumed)}</td>
                        <td className="text-end pe-4" style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{num(r.lot_count)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
